import { Injectable } from '@angular/core';
import { AppState } from '../app.state';
import { select, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, of, share, switchMap, withLatestFrom } from 'rxjs';
import { JobActions } from '@states/jobs/job.action-types';
import * as SharedActions from '@states/shared/shared.actions';
import { JobManagerService } from '../../services/job-manager.service';
import { UtilsService } from '../../edge/utils.service';
import { JobModels } from '@models/job.models';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class JobEffects {

  public beforeGetJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.beforeGetJobs),
      switchMap(() => [
        JobActions.setListLoader({ loading: true }),
        JobActions.getJobs(),
      ]),
      share(),
    ),
  );

  public getJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.getJobs),
      withLatestFrom(
        this.store$.pipe(select(state => state.jobState)),
      ),
      switchMap(([, { isLastPage, latestTs }]) => {
        return this.jobService
          .getJobs()
          .pipe(
            switchMap(res => {
              const groupedByQueue = res.reduce((acc, doc) => {
                if (!acc[doc.queue]) {
                  acc[doc.queue] = [];
                }
                acc[doc.queue].push(doc);
                return acc;
              }, {} as Record<string, JobModels.Document[]>);
              return [
                JobActions.setQueues({ queues: Object.keys(groupedByQueue) }),
                JobActions.getJobsSuccess({ documents: res }),
                JobActions.setListLoader({ loading: false }),
              ];
            }),
            catchError(err => {
              const errMsg = this.utilsService.errMessage(err);
              return [
                SharedActions.consoleMessage({ error: errMsg }),
                JobActions.setListLoader({ loading: false }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public removeJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.removeJob),
      switchMap(({ jobId, qName }) => {
        return this.jobService
          .removeJob(qName, jobId)
          .pipe(
            switchMap(res => {
              return [
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.REMOVE_JOB_SUCCESS') }),
                JobActions.removeJobSuccess({ jobId }),
              ];
            }),
            catchError(err => {
              const errMsg = this.utilsService.errMessage(err);
              return [
                SharedActions.showMessage({ error: this.translateService.instant('TOASTS.REMOVED_JOB_FAIL') }),
                SharedActions.consoleMessage({ error: errMsg }),
                JobActions.cleanRemovingId({ jobId }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  constructor(private store$: Store<AppState>,
              private actions$: Actions,
              private jobService: JobManagerService,
              private utilsService: UtilsService,
              private translateService: TranslateService) {
  }
}